/**
 * A menu anchored to a trigger, rendered into document.body.
 *
 * The toolbar scrolls horizontally rather than widening the page, and
 * `overflow` on an ancestor clips absolutely-positioned children — so a
 * dropdown declared inside it simply vanishes. Portalling out and positioning
 * with `fixed` coordinates taken from the trigger's rect sidesteps the whole
 * class of problem, and keeps the menu on screen when the trigger sits near an
 * edge.
 */
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export function Popover({
  anchorRef, open, onClose, children, align = "left", width,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  align?: "left" | "right";
  width?: number;
}) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const w = width ?? panelRef.current?.offsetWidth ?? 260;
    let left = align === "right" ? r.right - w : r.left;
    // Keep it inside the viewport rather than letting it run off the edge.
    left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
    setPos({ top: r.bottom + 6, left });
  }, [anchorRef, align, width]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => place();
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, place, onClose, anchorRef]);

  if (!open || !pos) {
    // Render once invisibly so the panel can be measured before it is placed.
    if (!open) return null;
  }

  return createPortal(
    <div
      ref={panelRef}
      className="popover"
      style={{
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        width: width ?? undefined,
        visibility: pos ? "visible" : "hidden",
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
